import React from 'react';
import { CheckCircle2, Clock, AlertTriangle, XCircle } from 'lucide-react';
import { Badge } from './badge';

/**
 * BankingStatusBadge - Affiche le statut de rapprochement bancaire d'une facture
 * 
 * Props:
 * - statut_paiement: "payee" | "Payée" | "non_paye" | "En attente" | etc.
 * - rapprochement_status: "rapproche_auto" | "rapproche_manuel" | "en_attente" | null
 * - echeance: Date d'échéance (optionnel, pour calculer le retard)
 * - compact: boolean - Version compacte pour les tableaux
 */
export default function BankingStatusBadge({ 
  statut_paiement, 
  rapprochement_status,
  echeance,
  compact = false 
}) {
  // Déterminer si payé
  const isPaid = ['payee', 'Payée', 'payé', 'paid'].includes(statut_paiement);
  
  // Déterminer si rejeté (prélèvement SEPA revenu impayé)
  const isRejected = ['rejetee', 'Rejetée', 'rejete', 'rejected'].includes(statut_paiement);
  
  // Calculer le retard si non payé et échéance passée
  const isOverdue = echeance && !isPaid && new Date(echeance) < new Date();
  const daysOverdue = isOverdue 
    ? Math.floor((new Date() - new Date(echeance)) / (1000 * 60 * 60 * 24))
    : 0;

  if (isRejected) {
    // Rejetée - rouge
    return (
      <Badge className="bg-red-100 text-red-700 border-red-200" data-testid="banking-status-rejected">
        <XCircle className="w-3 h-3 mr-1" />
        Rejetée
      </Badge>
    );
  }

  if (isPaid) {
    // Payée - vert
    if (rapprochement_status === 'rapproche_auto') {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          {compact ? 'Auto' : 'Rapproché auto'}
        </Badge>
      );
    }
    if (rapprochement_status === 'rapproche_manuel') {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          {compact ? 'Manuel' : 'Rapproché manuel'}
        </Badge>
      );
    }
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        {compact ? 'Payée' : 'Payée ✓'}
      </Badge>
    );
  }

  // En retard - orange/rouge
  if (isOverdue) {
    return (
      <Badge className="bg-orange-100 text-orange-700 border-orange-200">
        <AlertTriangle className="w-3 h-3 mr-1" />
        {compact ? `+${daysOverdue}j` : `Échue +${daysOverdue}j`}
      </Badge>
    );
  }

  // En attente - gris
  return (
    <Badge variant="outline" className="text-gray-500">
      <Clock className="w-3 h-3 mr-1" />
      {compact ? 'Attente' : 'En attente'}
    </Badge>
  );
}

// Version pour afficher dans une colonne de tableau
export function BankingStatusCell({ invoice }) {
  return (
    <BankingStatusBadge
      statut_paiement={invoice.statut_paiement}
      rapprochement_status={invoice.rapprochement_status}
      echeance={invoice.echeance}
      compact={true}
    />
  );
}
